import { computed, inject, Injectable, Injector } from '@angular/core';

import { WcgFilterIndicatorsOptions } from '@shared/interfaces/wcg-table-utils.interface';
import {
  BaseGridViewState,
  BASE_GRID_VIEW_STATE,
} from '@data-access/extendables';

import { WcgAdvancedFilter, WcgMenuOption } from '@wcg/frontend-framework';
import { updateGridFilter } from '@data-access/shared';

import { mapSmeQcUserToMenuOption } from '../utils';

import {
  SubmissionListSignalState,
  SubmissionListItem,
  BatchAssignmentsPayload,
  AssignmentPayload,
  QuickFilterKey,
} from '../types';
import { PERSISTED_QUICK_FILTER_KEYS } from '../constants';
import { PICK_UP_CARD_SORT_PARAMS } from '../constants/general.constants';

@Injectable({
  providedIn: 'root',
})
export class SubmissionListFacadeService extends BaseGridViewState {
  private readonly _submissionGridStore =
    inject<SubmissionListSignalState>(BASE_GRID_VIEW_STATE);
  private readonly _injector = inject(Injector);

  readonly $submissions = computed(() =>
    this._submissionGridStore.$submissions(),
  );
  readonly $currentUrl = computed(() =>
    this._submissionGridStore.$currentUrl(),
  );

  readonly $submissionListScope = computed(() =>
    this._submissionGridStore.$submissionListScope(),
  );

  readonly $isUpdatingSMEQC = computed(() =>
    this._submissionGridStore.$isUpdatingSMEQC(),
  );

  readonly $isAllSelected = computed(() =>
    this._submissionGridStore.$isAllSelected(),
  );
  readonly $isIndeterminate = computed(() =>
    this._submissionGridStore.$isIndeterminate(),
  );
  readonly $selectedRows = computed(() =>
    this._submissionGridStore.$selectedRows(),
  );
  readonly $isInternal = computed(() =>
    this._submissionGridStore.$isInternal(),
  );

  readonly $quickFilterCounts = computed(() =>
    this._submissionGridStore.$quickFilterCounts(),
  );

  readonly $quickFilter = computed(() =>
    this._submissionGridStore.$quickFilter(),
  );

  readonly $smeList = computed(() => this._submissionGridStore.$smeList());
  readonly $qcList = computed(() => this._submissionGridStore.$qcList());

  readonly $smeUserListOptions = computed<WcgMenuOption[]>(() => {
    const smeList = this.$smeList() ?? [];

    return smeList.map((user) =>
      mapSmeQcUserToMenuOption(user, this._injector),
    );
  });

  readonly $qcUserListOptions = computed<WcgMenuOption[]>(() => {
    const qcList = this.$qcList() ?? [];

    return qcList.map((user) => mapSmeQcUserToMenuOption(user, this._injector));
  });

  /**
   * NOTE: We are going forward with using inject() instead of constructor injection
   * But Super class needs to have the parameters passed in constructor
   * which we cannot do in the parent class for this specific signal store.
   * Hence, we are using inject() here and passing the values to super()
   */
  constructor() {
    super();
  }

  clearSearchString(): void {
    this._submissionGridStore.updateSearchString('');
  }

  /**
   * Updates `$advancedFilters` state **without** triggering an HTTP request.
   *
   * Use this when the data has already been fetched with the correct filter
   * (e.g. via the resolver) and only the UI chip / indicator state needs to
   * reflect the active selection.
   *
   * @param filters - Flattened advanced-filter map to persist in the store.
   */
  patchAdvancedFilters(filters: WcgAdvancedFilter): void {
    this._submissionGridStore.updateAdvancedFilters(filters);
  }

  /**
   * Restores the backend chip saved in the view's `notification` marker, or resets to `all`.
   * Runs before the grid reload so the marker never reaches the list request.
   */
  override setSelectedView(view: WcgMenuOption, resetSelectView = false): void {
    // Must run first — applying the view replaces the filters read below.
    super.setSelectedView(view, resetSelectView);
    // Backend chips exist only on the home list.
    if (this.$submissionListScope() !== 'home') return;

    const filters = this._submissionGridStore.$advancedFilters();
    const key = Object.keys(filters).find((k) =>
      ['notification', 'notifications'].includes(k.toLowerCase()),
    );
    // Chips are saved as plain string tokens in the `notification` value.
    const value = key ? filters[key]?.value : undefined;
    const saved = Array.isArray(value) ? value.map(String) : [];
    const chip = saved.find((v): v is QuickFilterKey =>
      PERSISTED_QUICK_FILTER_KEYS.includes(v),
    );

    // A view without a chip resets to `all`, so it shows what it was saved with.
    this._submissionGridStore.setQuickFilter(chip ?? 'all');
    this._submissionGridStore.loadQuickFilterCounts();
    if (key && chip) {
      // The chip is a marker, not a notification filter — keep it off the request.
      this._submissionGridStore.updateAdvancedFilters({
        ...filters,
        [key]: { ...filters[key], value: saved.filter((v) => v !== chip) },
      });
      // Re-baseline so the stripped marker doesn't flag the view as modified.
      this._submissionGridStore.syncBaselineAdFilters();
    }
  }

  updateSubmissionGridFilter(filter: WcgFilterIndicatorsOptions) {
    const _constructFilter = updateGridFilter(
      filter,
      this._submissionGridStore.$advancedFilters(),
    );
    this.updateGrid(
      'filter',
      {
        filters: _constructFilter.filters,
        page: this._submissionGridStore.$pageView().page,
        pageSize: this._submissionGridStore.$pageView().pageSize,
      },
      true,
      false,
    );
  }

  toggleRow(row: SubmissionListItem, checked: boolean) {
    this._submissionGridStore.selectRow(row, checked);
  }

  toggleSelectAll(checked: boolean) {
    this._submissionGridStore.selectAll(checked);
  }

  resetSelectedRows(): void {
    this._submissionGridStore.resetSelectedRows();
  }

  batchUpdateWorkspaceSMEQC(assignments: BatchAssignmentsPayload): void {
    this._submissionGridStore.batchUpdateWorkspaceSMEQC(assignments);
  }

  batchRemoveSMEQC(assignments: BatchAssignmentsPayload): void {
    this._submissionGridStore.batchRemoveSMEQC(assignments);
  }

  updateWorkspaceSMEQC(assignment: AssignmentPayload): void {
    this._submissionGridStore.updateWorkspaceSMEQC(assignment);
  }

  removeWorkspaceSMEQC(assignment: AssignmentPayload): void {
    this._submissionGridStore.removeWorkspaceSMEQC(assignment);
  }

  reloadSubmissionList(updateColumns?: boolean): void {
    this._submissionGridStore.reloadSubmissionList(updateColumns);
  }

  /** Fetches quick-filter bucket counts for the current list scope. */
  loadQuickFilterCounts(): void {
    this._submissionGridStore.loadQuickFilterCounts();
  }

  /** Sets the active quick filter; applied to every subsequent list load. */
  setQuickFilter(key: QuickFilterKey): void {
    this._submissionGridStore.setQuickFilter(key);
  }

  /**
   * Drops the Pick up card's sort (the viewer's own last edit), so the
   * list goes back to its default order. Any other sort is kept.
   */
  clearPickUpCardSort(): void {
    const isPickUpCardSort =
      this._submissionGridStore.$sort()?.sortByField ===
      PICK_UP_CARD_SORT_PARAMS.sort_by_field;
    if (isPickUpCardSort) {
      this._submissionGridStore.updateSort(null);
    }
  }
}
